import { useDocumentTitle, useIsomorphicEffect } from '@mantine/hooks';

// Mirrors the <title> in public/index.html. When a page unmounts we restore
// this so the tab title never "sticks" on a subsequent page that sets none.
export const DEFAULT_DOCUMENT_TITLE = 'Meals You Love - Meal plans built around your life';

/**
 * Sets the document (tab) title for the current page and automatically restores
 * the default title when the page unmounts.
 *
 * This is the canonical title hook for the app — prefer it over Mantine's
 * `useDocumentTitle` directly. Mantine only ever *sets* the title and never
 * restores it, so a page that set a title would leave it in place when you
 * navigate to a page that sets none, producing a stale/"sticky" title. By
 * resetting to the default on unmount, any page that doesn't set its own title
 * falls back to the index.html default instead of inheriting the previous one.
 *
 * Pass an empty string to leave the current title untouched (Mantine ignores
 * blank values). This is intentional for server-rendered routes (recipe / meal
 * plan / profile) whose authoritative <title> is injected server-side on full
 * load — they pass '' until their data loads so we don't clobber it, then set a
 * data-driven title for client-side navigation.
 */
export function usePageTitle(title: string) {
  useDocumentTitle(title);

  useIsomorphicEffect(
    () => () => {
      document.title = DEFAULT_DOCUMENT_TITLE;
    },
    [],
  );
}
